import { useEffect, useState } from "react";
import { Page } from "@/ui/Layout/Page";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { BreadcrumbItemTypes } from "@/ui/Breadcrumbs/types";
import "./agentsDark.css"; // dark mode for the Agents screens and the live room
import { ActiveClient, useActiveClientChrome, setActiveClient } from "./activeClient";

export type AppKey = "home" | "calendar" | "meetings" | "campaigns" | "kb" | "reviews" | "agent-activity" | "settings";

interface AppShellV8Props {
  children: React.ReactNode;
  breadcrumb: string[];
  headerActions?: React.ReactNode;
  /** Kept for source-compat with the 12 screens; navigation lives in the platform
   *  LeftSidebar rail, so these are no longer used. */
  activeApp?: AppKey;
  reviewCount?: number;
}

/**
 * Thin adapter that renders the Agents screens inside the platform-native Page +
 * Topbar (so the top bar matches the rest of Launch OS). It maps the prototype's
 * props onto `topBar`:
 *   - the active client becomes the first breadcrumb crumb (colored-initials chip)
 *   - `breadcrumb[]` become the remaining crumbs
 *   - `headerActions` become the right-side `items`
 * Global ⌘K search (CmdK in MainContentWrapper) replaces the old search button; the
 * Copilot ✨ button is the Topbar default (kept). The old per-screen left rail and
 * header were removed when the v8 prototype merged into the platform.
 */
// Top-level screen labels → routes, so intermediate breadcrumb crumbs navigate.
const SCREEN_HREFS: Record<string, string> = {
  Campaigns: "/agents/campaigns",
  Meetings: "/agents/meetings",
  Calendar: "/agents/calendar",
  Reviews: "/agents/reviews",
  "Knowledge Base": "/agents/knowledge-base",
  Settings: "/agents/settings",
};

export function AppShellV8({ children, breadcrumb, headerActions }: AppShellV8Props) {
  const client = useActiveClientChrome(); // reactive: the client crumb updates on switch
  const [clients, setClients] = useState<ActiveClient[]>([]);

  // Roster for the top-bar client switcher (E2). Same source as the LeftSidebar switcher.
  useEffect(() => {
    fetch(`${DOCS_API_DOMAIN}/api/agents/clients`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) =>
        setClients(((d.items || []) as any[]).map((c) => ({ slug: c.slug, name: c.name, initials: c.initials, color: c.color, primaryColor: c.primaryColor || undefined })))
      )
      .catch(() => setClients([]));
  }, []);

  // Hide the platform chat widget (HubSpot) for the agent tier during the trial —
  // it floats over the bottom-right of the app and isn't relevant to agents. Only
  // agent routes mount this shell, so this is effectively agent-only. Persisted
  // (id-guarded) to avoid flicker as you navigate between agent screens; also
  // covers Intercom selectors defensively in case the platform widget changes.
  useEffect(() => {
    if (document.getElementById("agents-hide-chat-widget")) return;
    const style = document.createElement("style");
    style.id = "agents-hide-chat-widget";
    style.textContent =
      '#hubspot-messages-iframe-container,[id^="hubspot-messages"],[class*="intercom"],#intercom-container{display:none !important;}';
    document.head.appendChild(style);
  }, []);

  // E2 (AMB-350): the active client is a dropdown switcher crumb (caret, via the
  // Breadcrumbs MENU type) — discoverable as the workspace switcher rather than looking
  // like a static label. "All Clients" leads, then the real roster. Switching is
  // app-wide (setActiveClient) and persists, identical to the LeftSidebar switcher.
  const ALL_CLIENTS: ActiveClient = { slug: "all", name: "All Clients", initials: "AG", color: "bg-gray-700" };
  const clientOptions: ActiveClient[] = [ALL_CLIENTS, ...clients.filter((c) => c.slug !== "all")];
  const clientCrumb = {
    type: BreadcrumbItemTypes.MENU,
    label: client.name,
    items: clientOptions.map((c) => ({
      label: c.name,
      icon: (
        <span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full ${c.primaryColor ? "" : c.color} text-[8px] font-bold text-white`} style={c.primaryColor ? { backgroundColor: c.primaryColor } : undefined}>{c.initials}</span>
      ),
    })),
    onClick: (key: number) => {
      const c = clientOptions[key];
      if (c && c.slug !== client.slug) setActiveClient(c);
    },
  };

  // AMB-407: a leading Home crumb so every screen has an obvious way back to Home
  // (Ryan/Kyle: "no way to get back to the home screen" from My Tasks etc.). The platform
  // breadcrumb uses react-router Links, so this is SPA navigation. Skipped on Home itself.
  // Icon only (no "Home" text) per Kyle — the house icon is enough on deep-linked pages.
  const homeCrumb = {
    label: "",
    href: "/agents/home",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-label="Home">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    ),
  };
  const page = [
    ...(breadcrumb[0] !== "Home" ? [homeCrumb] : []),
    clientCrumb,
    // Intermediate crumbs link to their screen; the last crumb (current page) stays plain.
    ...breadcrumb.map((label, i) => {
      const href = i < breadcrumb.length - 1 ? SCREEN_HREFS[label] : undefined;
      return href ? { label, href } : { label };
    }),
  ];

  // Copilot ✨ button + the "Ask Copilot" ⌘K search are not wired for the trial —
  // hide both across all Agents screens.
  // "agents-app" scopes the Agents dark-mode colors (agentsDark.css); display: contents keeps the layout as is
  return (
    <Page topBar={{ page, items: headerActions, hideCopilotButton: true, hideSearch: true }}>
      <div className="agents-app" style={{ display: "contents" }}>{children}</div>
    </Page>
  );
}
